import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Button,
  Tooltip,
  Table,
  Spin,
  Tabs,
  Progress,
  Empty,
  Input,
} from 'antd';
import { EyeOutlined, EditOutlined, RightOutlined, PlusOutlined, SearchOutlined } from '@ant-design/icons';
import { clientsApi, contracts as contractsApi, artifactApi } from '../api';
import TableComponent from '../other/TableComponent';
import {
  handelDate,
  handlestatus,
  extractContract,
} from './../other/usefulFunctions';
import SelectStatus from '../other/SelectStatus';
import ClientDrawer from './ClientDrawer';
import NdaDrawer from './NdaDrawer';
import MsaDrawer from './MsaDrawer';
import { userRole, employeeId } from '../../GetRole.utils';
import './styles.css';
import './clientDetail.css';

const STATUS = { draft: 1, pending: 2, active: 3 };

const columnsPoc = [
  { title: 'Type', dataIndex: 'pocType', key: 'pocType', width: '20%' },
  { title: 'Name', dataIndex: 'pocName', key: 'pocName', width: '25%' },
  {
    title: 'Email',
    dataIndex: 'pocEmail',
    key: 'pocEmail',
    width: '30%',
    render: (email) => (email ? <a href={`mailto:${email}`}>{email}</a> : '-'),
  },
  { title: 'Contact number', dataIndex: 'pocNumber', key: 'pocNumber', width: '25%' },
];

const columnsGSTIN = [
  {
    title: 'GSTIN',
    dataIndex: 'GSTIN',
    key: 'GSTIN',
    width: '18%',
    render: (_, record) => (record?.GSTIN ? record?.GSTIN : '-'),
  },
  { title: 'Address', dataIndex: 'address', key: 'address', width: '37%' },
  { title: 'City', dataIndex: 'city', key: 'city', width: '15%' },
  { title: 'State', dataIndex: 'state', key: 'state', width: '15%' },
  { title: 'Country', dataIndex: 'country', key: 'country', width: '15%' },
];

const initials = (name = '') =>
  name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join('');

const joinAddress = (parts) => parts.filter(Boolean).join(', ');

const emptyFilters = {
  page: 1,
  filterField: '',
  sorting: { field: '', order: '' },
  filterValue: '',
};

// TableComponent reports its filters on every render; only update state when they change
const mergeFilters = (prevState, value) =>
  prevState.page !== value.page ||
  prevState.filterField !== value.filterField ||
  prevState.sorting.field !== value.sorting.field ||
  prevState.sorting.order !== value.sorting.order ||
  prevState.filterValue !== value.filterValue
    ? {
        page: value.page,
        filterField: value.filterField,
        sorting: { field: value.sorting.field, order: value.sorting.order },
        filterValue: value.filterValue,
      }
    : prevState;

// ------------------------------------------------------------ contracts tab
const viewColumn = (navigate) => ({
  title: '',
  dataIndex: 'action',
  key: 'action',
  width: 70,
  fixed: 'right',
  render: (_, record) => (
    <Tooltip title="View">
      <Button
        type="text"
        aria-label="View contract"
        icon={<EyeOutlined />}
        onClick={() => navigate(`/contract/${record.contractTypeForUrl}/${record.key}`)}
      />
    </Tooltip>
  ),
});

const ClientContracts = ({ clientId, typeFilter, onClearType }) => {
  const navigate = useNavigate();

  const [masterFilters, setMasterFilters] = useState(emptyFilters);
  const [masterStatus, setMasterStatus] = useState([STATUS.active]);
  const [masterRows, setMasterRows] = useState([]);
  const [masterTotal, setMasterTotal] = useState();
  const [masterLoading, setMasterLoading] = useState(false);

  const [otherFilters, setOtherFilters] = useState(emptyFilters);
  const [otherStatus, setOtherStatus] = useState([STATUS.active, STATUS.pending]);
  const [otherRows, setOtherRows] = useState([]);
  const [otherTotal, setOtherTotal] = useState();
  const [otherLoading, setOtherLoading] = useState(false);

  const [masterSearchInput, setMasterSearchInput] = useState('');
  const [masterGlobalSearch, setMasterGlobalSearch] = useState('');
  const [otherSearchInput, setOtherSearchInput] = useState('');
  const [otherGlobalSearch, setOtherGlobalSearch] = useState('');

  
  useEffect(() => {
    const timer = setTimeout(() => setMasterGlobalSearch(masterSearchInput.trim()), 400);
    return () => clearTimeout(timer);
  }, [masterSearchInput]);

  useEffect(() => {
    const timer = setTimeout(() => setOtherGlobalSearch(otherSearchInput.trim()), 400);
    return () => clearTimeout(timer);
  }, [otherSearchInput]);

  useEffect(() => {
    setMasterLoading(true);
    clientsApi
      .getClientData(
        clientId,
        masterFilters.page,
        masterStatus,
        masterFilters.sorting.field,
        masterFilters.sorting.order,
        masterFilters.filterField,
        masterFilters.filterValue
      )
      .then((res) => {
        setMasterTotal(res.data.totalCount);
        setMasterRows(
          (res.data.mContracts || []).map((contract) => ({
            key: contract.contractId,
            contractName: extractContract(contract?.contractName),
            fileName: contract.fileName,
            fileUrl: contract.fileUrl,
            activationDate: handelDate(contract.activationDate),
            expiryDate: handelDate(contract.expiryDate),
            cStatus: contract.cStatus,
            contractTypeForUrl: extractContract(contract.contractName).toLowerCase(),
          }))
        );
      })
      .catch((error) => console.error('Error fetching data:', error))
      .finally(() => setMasterLoading(false));
  }, [clientId, masterStatus, masterFilters]);

  useEffect(() => {
    setOtherLoading(true);
    clientsApi
      .getClientOtherContracts(
        clientId,
        otherFilters.page,
        otherStatus,
        otherFilters.sorting.field,
        otherFilters.sorting.order,
        otherFilters.filterField,
        otherFilters.filterValue
      )
      .then((res) => {
        setOtherTotal(res.data.totalCount);
        setOtherRows(
          (res.data.mContracts || []).map((contract) => ({
            key: contract.contractId,
            contractName: extractContract(contract.contractName),
            activationDate: handelDate(contract.activationDate),
            expiryDate: handelDate(contract.expiryDate),
            projectName: contract.projectName,
            cStatus: contract.cStatus,
            contractTypeForUrl: extractContract(contract.contractName).toLowerCase(),
          }))
        );
      })
      .catch((error) => console.error('Error fetching data:', error))
      .finally(() => setOtherLoading(false));
  }, [clientId, otherStatus, otherFilters]);

  const masterColumns = [
    { title: 'Type', dataIndex: 'contractName', key: 'contractName', width: '12%' },
    {
      title: 'File',
      dataIndex: 'fileName',
      key: 'fileName',
      width: '28%',
      render: (_, record) => (
        <a target="_blank" rel="noreferrer" href={record.fileUrl}>
          {record.fileName}
        </a>
      ),
    },
    { title: 'Start date', dataIndex: 'activationDate', key: 'activationDate', width: '15%' },
    { title: 'End date', dataIndex: 'expiryDate', key: 'expiryDate', width: '15%' },
    {
      title: 'Status',
      dataIndex: 'cStatus',
      key: 'cStatus',
      width: '15%',
      render: (_, { cStatus }) => handlestatus(cStatus),
    },
    viewColumn(navigate),
  ];

  const otherColumns = [
    { title: 'Type', dataIndex: 'contractName', key: 'contractName', width: '12%' },
    { title: 'Project', dataIndex: 'projectName', key: 'projectName', width: '28%' },
    {
      title: 'Start date',
      dataIndex: 'activationDate',
      key: 'activationDate',
      width: '15%',
      render: (value) => value || '-',
    },
    {
      title: 'End date',
      dataIndex: 'expiryDate',
      key: 'expiryDate',
      width: '15%',
      render: (value) => value || '-',
    },
    {
      title: 'Status',
      dataIndex: 'cStatus',
      key: 'cStatus',
      width: '15%',
      render: (_, { cStatus }) => handlestatus(cStatus),
    },
    viewColumn(navigate),
  ];

  const visibleMasterRows = typeFilter
    ? masterRows.filter((r) => r.contractName?.toLowerCase() === typeFilter)
    : masterRows;

  return (
    <div className="c360-stack">
      <section className="c360-card">
        <div className="c360-card-head">
          <div>
            <h2 className="c360-card-title">Client agreements</h2>
            <p className="c360-card-sub">
              {typeFilter
                ? `Showing ${typeFilter.toUpperCase()} only.`
                : 'NDA and MSA cover every project of this client.'}
            </p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {typeFilter && (
              <Button type="link" onClick={onClearType}>
                Show all
              </Button>
            )}
            <Input
              allowClear
              prefix={<SearchOutlined style={{ color: '#98A2B3' }} />}
              placeholder="Search by Type, File or Status"
              value={masterSearchInput}
              onChange={(e) => setMasterSearchInput(e.target.value)}
              style={{ width: 260 }}
            />
            <SelectStatus
              onStatusChange={(values) => {
                setMasterStatus(values);
                setMasterRows([]);
              }}
            />
          </div>
        </div>
        <TableComponent
          loading={masterLoading}
          data={visibleMasterRows}
          columns={masterColumns}
          scrollX={900}
          scrollY={280}
          totalPages={typeFilter ? visibleMasterRows.length : masterTotal}
          onFilters={(value) => setMasterFilters((prev) => mergeFilters(prev, value))}
          disableColumnSearch
          searchField="global"
          searchValue={masterGlobalSearch}
        />
      </section>

      <section className="c360-card">
        <div className="c360-card-head">
          <div>
            <h2 className="c360-card-title">Project contracts</h2>
            <p className="c360-card-sub">SOWs, purchase orders and change requests.</p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Input
              allowClear
              prefix={<SearchOutlined style={{ color: '#98A2B3' }} />}
              placeholder="Search by Type, Project or Status"
              value={otherSearchInput}
              onChange={(e) => setOtherSearchInput(e.target.value)}
              style={{ width: 260 }}
            />
            <SelectStatus
              defaultStatuses={['success', 'warning']}
              onStatusChange={(values) => {
                setOtherStatus(values);
                setOtherRows([]);
              }}
            />
          </div>
        </div>
        <TableComponent
          loading={otherLoading}
          data={otherRows}
          columns={otherColumns}
          scrollX={900}
          scrollY={280}
          totalPages={otherTotal}
          onFilters={(value) => setOtherFilters((prev) => mergeFilters(prev, value))}
          disableColumnSearch
          searchField="global"
          searchValue={otherGlobalSearch}
        />
      </section>
    </div>
  );
};

// ---------------------------------------------------------- project cards
const ProjectCard = ({ project, summary, onOpen }) => {
  const percent = summary
    ? Math.round((summary.complete / Math.max(summary.total, 1)) * 100)
    : 0;
  return (
    <button type="button" className="c360-project" onClick={onOpen}>
      <div className="c360-project-head">
        <span className="c360-project-name">{project.projectName}</span>
        <RightOutlined className="c360-project-arrow" />
      </div>
      {summary ? (
        <>
          <div className="c360-project-meta">
            <span>Artifacts</span>
            <span>
              {summary.complete}/{summary.total}
            </span>
          </div>
          <Progress percent={percent} showInfo={false} size="small" strokeColor="#2952CC" />
          {(summary.overdue > 0 || summary.inReview > 0) && (
            <div className="c360-project-flags">
              {summary.overdue > 0 && (
                <span className="c360-pill c360-pill-danger">{summary.overdue} overdue</span>
              )}
              {summary.inReview > 0 && (
                <span className="c360-pill c360-pill-warning">{summary.inReview} in review</span>
              )}
            </div>
          )}
        </>
      ) : (
        <span className="c360-project-meta">Artifacts not set up</span>
      )}
    </button>
  );
};

// -------------------------------------------------------------- page
const ClientDetail = () => {
  const navigate = useNavigate();
  const { id: clientId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'overview';

  const [clientData, setClientData] = useState(null);
  const [availability, setAvailability] = useState({ nda: 'NO', msa: 'NO' });
  const [pageLoading, setPageLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [summaries, setSummaries] = useState({});
  const [projectContracts, setProjectContracts] = useState({ active: 0, pending: 0 });
  const [isEditOpen, setIsEditOpen] = useState(false);
  // { type: 'nda' | 'msa', contractId?, status? } — no contractId means a new agreement
  const [agreementDrawer, setAgreementDrawer] = useState(null);
  // NDA / MSA that is in draft or awaiting approval, so we continue it instead of creating another
  const [openAgreements, setOpenAgreements] = useState({ nda: null, msa: null });
  const [contractsVersion, setContractsVersion] = useState(0);

  const canEdit = userRole !== 'finance';

  const closeAgreementDrawer = (saved) => {
    setAgreementDrawer(null);
    if (saved) {
      loadClient();
      setContractsVersion((v) => v + 1);
    }
  };

  const loadClient = useCallback(() => {
    clientsApi
      .getClientData(clientId, 1, [STATUS.active])
      .then((res) => {
        const data = res.data;
        setClientData(data.data[0] || {});
        setAvailability({
          nda: data.availability[0]?.ndaAvailability || 'NO',
          msa: data.availability[0]?.msaAvailability || 'NO',
        });
      })
      .catch((error) => console.error('Error fetching client:', error))
      .finally(() => setPageLoading(false));

    clientsApi
      .getClientData(clientId, 1, [STATUS.draft, STATUS.pending])
      .then((res) => {
        const rows = res.data.mContracts || [];
        // a pending one wins over a draft: it is the one people should look at
        const pick = (type) => {
          const ofType = rows.filter((r) => extractContract(r.contractName).toLowerCase() === type);
          return ofType.find((r) => r.cStatus === 'pendingapproval') || ofType[0] || null;
        };
        setOpenAgreements({ nda: pick('nda'), msa: pick('msa') });
      })
      .catch((error) => console.error('Error fetching open agreements:', error));
  }, [clientId]);

  useEffect(() => {
    setPageLoading(true);
    loadClient();
  }, [loadClient]);

  // projects of this client, then each project's artifact progress
  useEffect(() => {
    let cancelled = false;
    contractsApi
      .getProjectDropDown(clientId)
      .then((res) => {
        if (cancelled) return;
        const list = res.data || [];
        setProjects(list);
        return Promise.allSettled(
          list.map((p) => artifactApi.getProjectArtifacts(p.projectId, employeeId))
        ).then((results) => {
          if (cancelled) return;
          const next = {};
          results.forEach((result, i) => {
            if (result.status === 'fulfilled') {
              next[list[i].projectId] = result.value.data.summary;
            }
          });
          setSummaries(next);
        });
      })
      .catch((error) => console.error('Error fetching projects:', error));
    return () => {
      cancelled = true;
    };
  }, [clientId]);

  // counts of active / pending project contracts for the lifecycle strip
  useEffect(() => {
    Promise.allSettled([
      clientsApi.getClientOtherContracts(clientId, 1, [STATUS.active]),
      clientsApi.getClientOtherContracts(clientId, 1, [STATUS.pending]),
    ]).then(([active, pending]) => {
      setProjectContracts({
        active: active.status === 'fulfilled' ? Number(active.value.data.totalCount) || 0 : 0,
        pending: pending.status === 'fulfilled' ? Number(pending.value.data.totalCount) || 0 : 0,
      });
    });
  }, [clientId]);

  const setTab = (key, type) => {
    const next = new URLSearchParams(searchParams);
    if (key === 'overview') next.delete('tab');
    else next.set('tab', key);
    if (type) next.set('docType', type);
    else next.delete('docType');
    setSearchParams(next, { replace: true });
  };

  if (pageLoading && !clientData) {
    return (
      <div className="c360-loading">
        <Spin />
      </div>
    );
  }

  const client = clientData || {};
  const isIndia = client.seId === 1;
  const primaryGst = client.GSTINDetails?.[0];
  const address = isIndia
    ? joinAddress([primaryGst?.address, primaryGst?.city, primaryGst?.state, primaryGst?.country, primaryGst?.pinCode])
    : joinAddress([client.address, client.city, client.state, client.country, client.pinCode]);
  const contacts = client.poc || [];
  const primaryContact = contacts[0];

  const artifactTotals = Object.values(summaries).reduce(
    (acc, s) => ({ complete: acc.complete + (s?.complete || 0), total: acc.total + (s?.total || 0) }),
    { complete: 0, total: 0 }
  );

  const agreementStep = (type, step, name) => {
    const active = availability[type] === 'YES';
    const open = openAgreements[type];
    return {
      step,
      name,
      ok: active,
      warn: !active && !!open,
      state: active
        ? 'Active'
        : open?.cStatus === 'pendingapproval'
        ? 'Awaiting approval'
        : open
        ? 'Draft in progress'
        : 'Missing',
      onClick: () => setTab('contracts', type),
    };
  };

  // what the header button does for an NDA / MSA that is not active yet
  const agreementAction = (type) => {
    if (availability[type] === 'YES') return null;
    const label = type.toUpperCase();
    const open = openAgreements[type];
    if (open?.cStatus === 'pendingapproval') {
      return {
        label: `${label} awaiting approval`,
        icon: <EyeOutlined />,
        onClick: () => navigate(`/contract/${type}/${open.contractId}`),
      };
    }
    if (open) {
      return {
        label: `Continue ${label} draft`,
        icon: <EditOutlined />,
        onClick: () => setAgreementDrawer({ type, contractId: open.contractId, status: 'draft' }),
      };
    }
    return {
      label: `New ${label}`,
      icon: <PlusOutlined />,
      onClick: () => setAgreementDrawer({ type }),
    };
  };

  const agreementActions = ['nda', 'msa']
    .map((type) => ({ type, action: agreementAction(type) }))
    .filter((a) => a.action);

  const lifecycle = [
    agreementStep('nda', 'Step 1', 'NDA'),
    agreementStep('msa', 'Step 2', 'MSA'),
    {
      step: 'Step 3',
      name: 'Projects',
      ok: projects.length > 0,
      state: projects.length ? `${projects.length} project${projects.length === 1 ? '' : 's'}` : 'None yet',
      onClick: () => setTab('projects'),
    },
    {
      step: 'Step 4',
      name: 'SOW · PO · CR',
      ok: projectContracts.active > 0 && projectContracts.pending === 0,
      warn: projectContracts.pending > 0,
      state:
        `${projectContracts.active} active` +
        (projectContracts.pending ? ` · ${projectContracts.pending} pending` : ''),
      onClick: () => setTab('contracts'),
    },
    {
      step: 'Step 5',
      name: 'Delivery artifacts',
      ok: artifactTotals.total > 0 && artifactTotals.complete === artifactTotals.total,
      progress: artifactTotals.total > 0 && artifactTotals.complete < artifactTotals.total,
      state: artifactTotals.total
        ? `${artifactTotals.complete} of ${artifactTotals.total} complete`
        : 'Not started',
      onClick: () => setTab('projects'),
    },
  ];

  const stepTone = (s) => (s.ok ? 'done' : s.progress ? 'progress' : s.warn ? 'warn' : 'todo');

  const projectGrid = projects.length ? (
    <div className="c360-project-grid">
      {projects.map((p) => (
        <ProjectCard
          key={p.projectId}
          project={p}
          summary={summaries[p.projectId]}
          onOpen={() => navigate(`/projects/${p.projectId}`)}
        />
      ))}
    </div>
  ) : (
    <Empty
      image={Empty.PRESENTED_IMAGE_SIMPLE}
      description="No projects for this client yet"
      style={{ padding: '24px 0' }}
    >
      <Button onClick={() => navigate('/projects')}>Go to projects</Button>
    </Empty>
  );

  const tabs = [
    {
      key: 'overview',
      label: 'Overview',
      children: (
        <div className="c360-cols">
          <section className="c360-main">
            <div className="c360-section-head">
              <h2 className="c360-card-title">Projects</h2>
              {projects.length > 0 && (
                <Button type="link" onClick={() => setTab('projects')}>
                  View all
                </Button>
              )}
            </div>
            {projectGrid}
          </section>

          <aside className="c360-side">
            <section className="c360-card">
              <h2 className="c360-card-title">Company details</h2>
              <dl className="c360-dl">
                <dt>Contracting entity</dt>
                <dd>{isIndia ? 'Spanidea Systems Pvt. Ltd.' : 'Spanidea Systems LLC'}</dd>
                <dt>{isIndia ? 'Primary address' : 'Address'}</dt>
                <dd>{address || '-'}</dd>
                {isIndia ? (
                  <>
                    <dt>Primary GSTIN</dt>
                    <dd>{primaryGst?.GSTIN || 'N/A'}</dd>
                    <dt>TAN number</dt>
                    <dd>{client.TANNumber || '-'}</dd>
                  </>
                ) : (
                  <>
                    <dt>Tax ID</dt>
                    <dd>{client.TaxId || '-'}</dd>
                  </>
                )}
              </dl>
            </section>

            <section className="c360-card">
              <div className="c360-section-head">
                <h2 className="c360-card-title">Primary contact</h2>
                {contacts.length > 1 && (
                  <Button type="link" onClick={() => setTab('contacts')}>
                    All {contacts.length}
                  </Button>
                )}
              </div>
              {primaryContact ? (
                <div className="c360-contact">
                  <span className="c360-contact-name">{primaryContact.pocName}</span>
                  <span className="c360-muted">{primaryContact.pocType}</span>
                  {primaryContact.pocEmail && (
                    <a href={`mailto:${primaryContact.pocEmail}`}>{primaryContact.pocEmail}</a>
                  )}
                  {primaryContact.pocNumber && (
                    <span className="c360-muted">{primaryContact.pocNumber}</span>
                  )}
                </div>
              ) : (
                <span className="c360-muted">No contact added</span>
              )}
            </section>
          </aside>
        </div>
      ),
    },
    {
      key: 'contracts',
      label: 'Contracts',
      children: (
        <ClientContracts
          key={contractsVersion}
          clientId={clientId}
          typeFilter={searchParams.get('docType')}
          onClearType={() => setTab('contracts')}
        />
      ),
    },
    {
      key: 'projects',
      label: (
        <span>
          Projects <span className="c360-tab-count">{projects.length}</span>
        </span>
      ),
      children: projectGrid,
    },
    {
      key: 'contacts',
      label: (
        <span>
          Contacts <span className="c360-tab-count">{contacts.length}</span>
        </span>
      ),
      children: (
        <div className="c360-stack">
          <section className="c360-card">
            <h2 className="c360-card-title" style={{ marginBottom: 12 }}>
              Points of contact
            </h2>
            <Table
              columns={columnsPoc}
              dataSource={contacts.map((c, i) => ({ ...c, key: i }))}
              pagination={false}
              scroll={{ x: 640 }}
            />
          </section>
          {isIndia && (
            <section className="c360-card">
              <h2 className="c360-card-title" style={{ marginBottom: 12 }}>
                GST registrations
              </h2>
              <Table
                columns={columnsGSTIN}
                dataSource={(client.GSTINDetails || []).map((g, i) => ({ ...g, key: i }))}
                pagination={false}
                scroll={{ x: 640 }}
              />
            </section>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="c360">

      <section className="c360-header">
        <div className="c360-header-row">
          <div className="c360-avatar">{initials(client.legalEntityName)}</div>
          <div className="c360-title-block">
            <h1 className="c360-title">{client.legalEntityName}</h1>
            <div className="c360-subline">
              <span>{isIndia ? 'India' : 'International'}</span>
              {isIndia && primaryGst?.GSTIN && <span>GSTIN {primaryGst.GSTIN}</span>}
              {!isIndia && client.TaxId && <span>Tax ID {client.TaxId}</span>}
              {primaryContact?.pocName && <span>Contact: {primaryContact.pocName}</span>}
            </div>
          </div>
          {canEdit && (
            <div className="c360-actions">
              <Button icon={<EditOutlined />} onClick={() => setIsEditOpen(true)}>
                Edit details
              </Button>
              {agreementActions.map(({ type, action }, index) => (
                <Button
                  key={type}
                  type={index === 0 ? 'primary' : 'default'}
                  icon={action.icon}
                  onClick={action.onClick}
                >
                  {action.label}
                </Button>
              ))}
            </div>
          )}
        </div>

        <div className="c360-lifecycle">
          {lifecycle.map((s) => (
            <button
              key={s.name}
              type="button"
              className={`c360-step c360-step-${stepTone(s)}`}
              onClick={s.onClick}
            >
              <span className="c360-step-num">{s.step}</span>
              <span className="c360-step-name">{s.name}</span>
              <span className="c360-step-state">{s.state}</span>
            </button>
          ))}
        </div>
      </section>

      <Tabs activeKey={activeTab} onChange={setTab} items={tabs} className="c360-tabs" />

      <NdaDrawer
        open={agreementDrawer?.type === 'nda'}
        contractId={agreementDrawer?.contractId}
        status={agreementDrawer?.status}
        prefill={{ seId: client.seId, clientId: Number(clientId), legalEntityName: client.legalEntityName }}
        onClose={closeAgreementDrawer}
      />
      <MsaDrawer
        open={agreementDrawer?.type === 'msa'}
        contractId={agreementDrawer?.contractId}
        status={agreementDrawer?.status}
        prefill={{ seId: client.seId, clientId: Number(clientId), legalEntityName: client.legalEntityName }}
        onClose={closeAgreementDrawer}
      />

      <ClientDrawer
        open={isEditOpen}
        clientId={clientId}
        onClose={(saved) => {
          setIsEditOpen(false);
          if (saved) loadClient();
        }}
      />
    </div>
  );
};

export default ClientDetail;
